import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { apiGet, apiPost, apiDelete } from '../api';
import { useT } from '../i18n';
import DashboardLayout from '../components/DashboardLayout';

export default function FeedbackPage() {
  const navigate = useNavigate();
  const accessToken = useStore((s) => s.accessToken);
  const t = useT();

  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [about, setAbout] = useState('platform');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!accessToken) { navigate('/'); return; }
    loadFeedbacks(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const loadFeedbacks = async (p) => {
    setLoading(true);
    try {
      const res = await apiGet(`/api/v1/feedbacks/my?page=${p}&size=10`);
      const data = await res.json();
      if (data.success) {
        setFeedbacks(data.data?.content || []);
        setTotalPages(data.data?.totalPages || 0);
        setPage(p);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    setSubmitting(true);
    try {
      const res = await apiPost('/api/v1/feedbacks', { feedback: feedback.trim(), about });
      const data = await res.json();
      if (data.success) {
        setFeedback('');
        setAbout('platform');
        setShowForm(false);
        setMessage({ type: 'success', text: t('feedback.submitted') });
        loadFeedbacks(0);
      } else {
        setMessage({ type: 'error', text: data.message || t('feedback.error') });
      }
    } catch {
      setMessage({ type: 'error', text: t('feedback.error') });
    }
    setSubmitting(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const confirmDeleteFeedback = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await apiDelete(`/api/v1/feedbacks/${deleteConfirm}`);
      setFeedbacks((prev) => prev.filter((f) => f.id !== deleteConfirm));
    } catch { /* ignore */ }
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>

      {deleteConfirm && (
        <div className="confirm-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="#f85149" strokeWidth="2.5" fill="rgba(248,81,73,0.08)" />
                <path d="M24 14v12" stroke="#f85149" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="24" cy="32" r="1.5" fill="#f85149" />
              </svg>
            </div>
            <h3 className="confirm-title">{t('feedback.deleteTitle')}</h3>
            <p className="confirm-desc">{t('feedback.deleteConfirm')}</p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                {t('profile.cancel')}
              </button>
              <button className="confirm-delete" onClick={confirmDeleteFeedback} disabled={deleting}>
                {deleting ? t('delete.deleting') : t('delete.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="home-content">
        <div className="home-header">
          <div>
            <h1 className="home-title">{t('feedback.title')}</h1>
            <p className="home-subtitle">{t('feedback.subtitle')}</p>
          </div>
          <button className="home-new-btn" onClick={() => setShowForm(!showForm)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              {showForm ? <path d="M3 8h10" /> : <path d="M8 3v10M3 8h10" />}
            </svg>
            {showForm ? t('profile.cancel') : t('feedback.write')}
          </button>
        </div>

        {message && (
          <div className={`profile-message ${message.type}`}>{message.text}</div>
        )}

        {showForm && (
          <form className="feedback-form" onSubmit={handleSubmit}>
            <div className="feedback-form-card">
              <div className="profile-field">
                <label>{t('feedback.aboutLabel')}</label>
                <div className="feedback-about-options">
                  {['platform', 'generated_project', 'other'].map((val) => (
                    <button
                      key={val}
                      type="button"
                      className={`feedback-about-btn${about === val ? ' active' : ''}`}
                      onClick={() => setAbout(val)}
                    >
                      {val === 'platform' ? t('feedback.aboutPlatform')
                        : val === 'generated_project' ? t('feedback.aboutGenerated')
                        : t('feedback.aboutOther')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="profile-field">
                <label>{t('feedback.messageLabel')}</label>
                <textarea
                  className="feedback-textarea"
                  placeholder={t('feedback.placeholder')}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={4}
                  required
                />
              </div>
              <button className="home-new-btn" type="submit" disabled={submitting || !feedback.trim()}>
                {submitting ? t('feedback.submitting') : t('feedback.submit')}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="home-loading">{t('home.loading')}</div>
        ) : feedbacks.length === 0 ? (
          <div className="home-empty">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.4 }}>
              <rect x="8" y="8" width="40" height="40" rx="8" stroke="var(--text-secondary)" strokeWidth="2" />
              <path d="M18 24h20M18 32h12" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p>{t('feedback.noFeedbacks')}</p>
            <button className="home-empty-btn" onClick={() => setShowForm(true)}>{t('feedback.writeFirst')}</button>
          </div>
        ) : (
          <>
            <div className="feedback-list">
              {feedbacks.map((f) => (
                <div key={f.id} className="feedback-card">
                  <div className="feedback-card-top">
                    <span className={`feedback-about-tag ${f.about}`}>
                      {f.about === 'platform' ? t('feedback.aboutPlatform')
                        : f.about === 'generated_project' ? t('feedback.aboutGenerated')
                        : t('feedback.aboutOther')}
                    </span>
                    <button className="feedback-delete-btn" onClick={() => setDeleteConfirm(f.id)} title={t('delete.delete')}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M3 3l8 8M11 3l-8 8" />
                      </svg>
                    </button>
                  </div>
                  <p className="feedback-card-text">{f.feedback}</p>
                  <span className="feedback-card-date">{formatDate(f.createdAt)}</span>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="feedback-pagination">
                <button className="feedback-page-btn" disabled={page === 0} onClick={() => loadFeedbacks(page - 1)}>
                  &laquo; {t('feedback.prev')}
                </button>
                <span className="feedback-page-info">{page + 1} / {totalPages}</span>
                <button className="feedback-page-btn" disabled={page >= totalPages - 1} onClick={() => loadFeedbacks(page + 1)}>
                  {t('feedback.next')} &raquo;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
